import type { Vec2 } from '../types.js';

/**
 * Arena definition. Ported from the legacy `client/public/maps/arena_preview.json`
 * concept (a rectangular top-down arena with scattered stone obstacles) into a
 * compact, collision-authoritative form.
 *
 * `apps/client/public/maps/arena.tmj` is a Tiled map that mirrors these numbers
 * for rendering and visual editing. If you change the arena here, update the
 * Tiled file too (see README > "Editing the map").
 */
export interface ObstacleRect {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export const ARENA = {
  /** Tile size in world pixels. */
  tile: 50,
  /** Grid dimensions in tiles. */
  cols: 40,
  rows: 40,
  get width(): number {
    return this.tile * this.cols;
  },
  get height(): number {
    return this.tile * this.rows;
  },
} as const;

/**
 * Solid obstacles (walls / rocks). Axis-aligned rectangles in world pixels.
 * Kept deliberately few and chunky so pathfinding is cheap and routing is
 * visually obvious.
 */
export const OBSTACLES: ObstacleRect[] = [
  { id: 'rock-nw', x: 450, y: 450, w: 250, h: 150 },
  { id: 'rock-ne', x: 1300, y: 400, w: 150, h: 300 },
  { id: 'wall-center', x: 850, y: 950, w: 300, h: 100 },
  { id: 'rock-sw', x: 400, y: 1350, w: 200, h: 200 },
  { id: 'rock-se', x: 1350, y: 1350, w: 250, h: 150 },
  { id: 'pillar-1', x: 700, y: 700, w: 90, h: 90 },
  { id: 'pillar-2', x: 1200, y: 1150, w: 90, h: 90 },
];

/**
 * feature 5.2 — the obstacle set that collision (`resolveAgainstArena` /
 * `segmentClear`) and pathfinding (`buildBlockedGrid`) actually consult. Defaults
 * to the base `OBSTACLES`; `setActiveObstacles` swaps in a per-boss set (base +
 * that boss's pillars) for the duration of a boss fight and `resetActiveObstacles`
 * reverts it on wavebreak entry (§D14/D15). A monotonic generation counter lets
 * pathfinding rebuild its cached blocked grid only when the set actually changed.
 */
let activeObstacles: ObstacleRect[] = OBSTACLES;
let obstacleGeneration = 0;

/** feature 5.2 — the obstacle set collision + pathfinding consult right now. */
export function getActiveObstacles(): ObstacleRect[] {
  return activeObstacles;
}

/** feature 5.2 — override the active obstacle set (e.g. base + boss pillars). */
export function setActiveObstacles(rects: ObstacleRect[]): void {
  activeObstacles = rects;
  obstacleGeneration++;
}

/** feature 5.2 — revert to the base `OBSTACLES` (no-op if already reverted). */
export function resetActiveObstacles(): void {
  if (activeObstacles === OBSTACLES) return;
  activeObstacles = OBSTACLES;
  obstacleGeneration++;
}

/** feature 5.2 — bumped whenever the active obstacle set changes; pathfinding
 * rebuilds its cached blocked grid when this differs from its last-seen value. */
export function activeObstacleGeneration(): number {
  return obstacleGeneration;
}

/** Player spawn ring around the arena centre. */
export const PLAYER_SPAWNS: Vec2[] = (() => {
  const cx = ARENA.width / 2;
  const cy = ARENA.height / 2;
  const r = 140;
  const out: Vec2[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 - Math.PI / 2;
    out.push({ x: Math.round(cx + Math.cos(a) * r), y: Math.round(cy + Math.sin(a) * r) });
  }
  return out;
})();

/** Candidate enemy spawn points spread along the arena edges. */
export const ENEMY_SPAWNS: Vec2[] = (() => {
  const m = 60;
  const w = ARENA.width;
  const h = ARENA.height;
  const pts: Vec2[] = [];
  const steps = 8;
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) / steps;
    pts.push({ x: Math.round(t * w), y: m });
    pts.push({ x: Math.round(t * w), y: h - m });
    pts.push({ x: m, y: Math.round(t * h) });
    pts.push({ x: w - m, y: Math.round(t * h) });
  }
  return pts;
})();

function rectsOverlap(ax: number, ay: number, aw: number, ah: number, b: ObstacleRect): boolean {
  return ax < b.x + b.w && ax + aw > b.x && ay < b.y + b.h && ay + ah > b.y;
}

/**
 * Build the boolean blocked grid used by pathfinding. A tile is blocked when
 * any obstacle overlaps it, inflated by `pad` pixels so paths keep a small
 * clearance from walls (roughly the agent radius).
 */
export function buildBlockedGrid(pad = 18): boolean[][] {
  const grid: boolean[][] = [];
  for (let r = 0; r < ARENA.rows; r++) {
    const row: boolean[] = [];
    for (let c = 0; c < ARENA.cols; c++) {
      const x = c * ARENA.tile - pad;
      const y = r * ARENA.tile - pad;
      const size = ARENA.tile + pad * 2;
      row.push(activeObstacles.some((o) => rectsOverlap(x, y, size, size, o)));
    }
    grid.push(row);
  }
  return grid;
}

export function worldToCell(x: number, y: number): { c: number; r: number } {
  return {
    c: Math.max(0, Math.min(ARENA.cols - 1, Math.floor(x / ARENA.tile))),
    r: Math.max(0, Math.min(ARENA.rows - 1, Math.floor(y / ARENA.tile))),
  };
}

export function cellToWorld(c: number, r: number): Vec2 {
  return { x: c * ARENA.tile + ARENA.tile / 2, y: r * ARENA.tile + ARENA.tile / 2 };
}
